import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FolderOpen, Folder, ChevronDown, ChevronRight } from 'lucide-react';
import PresetTable from './PresetTable';

/**
 * FolderView - 프리셋을 tree_name별로 폴더 형태로 그룹핑하여 표시
 *
 * @param {Array} presets - 프리셋 목록
 * @param {boolean} showBookmark - 북마크 버튼 표시 여부
 * @param {boolean} isMyPresetTable - 내 프리셋 테이블 여부
 * @param {string} activeTab - 현재 활성 탭 ('myBookmarks', 'myPresets', 'publishedPresets')
 * @param {boolean} isMobileView - 모바일 뷰 여부
 * @param {number|null} highlightPresetId - 하이라이팅할 프리셋 ID
 * @param {Function} onToggleBookmark - 북마크 토글 함수
 * @param {Function} onPreview - 미리보기 함수
 * @param {Function} onCopy - 복사 함수
 * @param {Function} onEdit - 수정 함수
 * @param {Function} onDelete - 삭제 함수
 * @param {Function} onTogglePublish - 공개 상태 토글 함수
 * @param {Function} onViewExams - 시험 정보 조회 함수
 */
const FolderView = ({
  presets,
  showBookmark = true,
  isMyPresetTable = false,
  activeTab,
  isMobileView = false,
  highlightPresetId = null,
  onToggleBookmark,
  onPreview,
  onCopy,
  onEdit,
  onDelete,
  onTogglePublish,
  onViewExams
}) => {
  const storageKey = `presetFolders_${activeTab}`;
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey)) || {};
    } catch {
      return {};
    }
  });
  const hasAutoExpanded = useRef(false);

  // 프리셋을 tree_name + edu_ver 기준으로 그룹핑
  const groupedPresets = useMemo(() => {
    if (!presets || presets.length === 0) return {};

    return presets.reduce((acc, preset) => {
      // tree_name과 edu_ver을 조합하여 고유 키 생성
      const key = `${preset.tree_name}_${preset.edu_ver}`;

      if (!acc[key]) {
        acc[key] = {
          tree_name: preset.tree_name,
          edu_ver: preset.edu_ver,
          presets: [],
          count: 0
        };
      }

      acc[key].presets.push(preset);
      acc[key].count++;

      return acc;
    }, {});
  }, [presets]);

  // 폴더 키 목록 (tree_name 기준으로 정렬)
  const folderKeys = useMemo(() => {
    return Object.keys(groupedPresets).sort((a, b) => {
      const folderA = groupedPresets[a];
      const folderB = groupedPresets[b];

      // tree_name으로 먼저 정렬, 같으면 edu_ver로 정렬
      const nameCompare = folderA.tree_name.localeCompare(folderB.tree_name, 'ko');
      if (nameCompare !== 0) return nameCompare;

      return folderA.edu_ver.localeCompare(folderB.edu_ver, 'ko');
    });
  }, [groupedPresets]);

  // highlightPresetId가 있으면 해당 프리셋이 속한 폴더를 자동 확장
  useEffect(() => {
    if (!highlightPresetId || hasAutoExpanded.current) return;

    for (const key of folderKeys) {
      const folder = groupedPresets[key];
      const found = folder.presets.some(p => p.preset_id === highlightPresetId);
      if (found) {
        setExpandedFolders(prev => {
          const next = { ...prev, [key]: true };
          localStorage.setItem(storageKey, JSON.stringify(next));
          return next;
        });
        hasAutoExpanded.current = true;
        break;
      }
    }
  }, [highlightPresetId, folderKeys, groupedPresets, storageKey]);

  // 폴더 확장/축소 토글
  const toggleFolder = (key) => {
    setExpandedFolders(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  // 버튼 표시 여부 결정
  const userType = localStorage.getItem('userType');
  const isManager = userType === 'manager';
  const showEditButton = isMyPresetTable || (activeTab === 'publishedPresets' && isManager);
  const showCopyButton = activeTab === 'publishedPresets' || activeTab === 'myPresets';

  if (!presets || presets.length === 0) {
    return <p className="py-8 text-center text-gray-500">데이터 없음</p>;
  }

  return (
    <div className="space-y-2">
      {folderKeys.map(key => {
        const folder = groupedPresets[key];
        const isExpanded = expandedFolders[key];

        return (
          <div
            key={key}
            className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
          >
            {/* 폴더 헤더 */}
            <button
              onClick={() => toggleFolder(key)}
              className="flex w-full items-center justify-between px-4 py-3 whitespace-nowrap transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="size-5 text-gray-500" />
                ) : (
                  <ChevronRight className="size-5 text-gray-500" />
                )}
                {isExpanded ? (
                  <FolderOpen className="size-5 text-primary" />
                ) : (
                  <Folder className="size-5 text-gray-400" />
                )}
                <span className="font-medium text-gray-900">
                  {folder.tree_name}
                </span>
                <span className="text-sm text-gray-500">
                  ({folder.edu_ver})
                </span>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                {folder.count}개
              </span>
            </button>

            {/* 폴더 내용 (확장 시) */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                <PresetTable
                  presets={folder.presets}
                  showBookmark={showBookmark}
                  isMyPresetTable={isMyPresetTable}
                  showCopyButton={showCopyButton}
                  showEditButton={showEditButton}
                  highlightPresetId={highlightPresetId}
                  onToggleBookmark={onToggleBookmark}
                  onPreview={onPreview}
                  onCopy={onCopy}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onTogglePublish={onTogglePublish}
                  onViewExams={onViewExams}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FolderView;
